import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../api";

// ==================== Initial State ====================
const initialState = {
  treatmentPlans: [],
  currentPlan: null,
  isLoading: false,
  error: null,
};

// ==================== Async Thunks ====================

// Fetch all Treatment Plans (optional patient filter)
export const fetchTreatmentPlans = createAsyncThunk(
  "treatmentPlan/fetchAll",
  async (patientId = null, { rejectWithValue }) => {
    try {
      const url = patientId ? `/treatment-plan?patient=${patientId}` : "/treatment-plan";
      const response = await api.get(url);
      return response.data?.data ?? response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to fetch Treatment Plans"
      );
    }
  }
);

// Fetch single Treatment Plan
export const fetchTreatmentPlan = createAsyncThunk(
  "treatmentPlan/fetchOne",
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/treatment-plan/${id}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to fetch Treatment Plan"
      );
    }
  }
);

// Create Treatment Plan
export const createTreatmentPlan = createAsyncThunk(
  "treatmentPlan/create",
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post("/treatment-plan", data);
      return response.data.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || "Failed to create Treatment Plan"
      );
    }
  }
);

// Update Treatment Plan
export const updateTreatmentPlan = createAsyncThunk(
  "treatmentPlan/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/treatment-plan/${id}`, data);
      return response.data.data ?? response.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || "Failed to update Treatment Plan"
      );
    }
  }
);

// Delete Treatment Plan
export const deleteTreatmentPlan = createAsyncThunk(
  "treatmentPlan/delete",
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/treatment-plan/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to delete Treatment Plan"
      );
    }
  }
);

// ==================== Slice ====================
const treatmentPlanSlice = createSlice({
  name: "treatmentPlan",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentPlan: (state, action) => {
      state.currentPlan = action.payload;
    },
    clearCurrentPlan: (state) => {
      state.currentPlan = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // FETCH ALL
      .addCase(fetchTreatmentPlans.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTreatmentPlans.fulfilled, (state, action) => {
        state.isLoading = false;
        state.treatmentPlans = action.payload;
      })
      .addCase(fetchTreatmentPlans.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // FETCH ONE
      .addCase(fetchTreatmentPlan.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTreatmentPlan.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentPlan = action.payload;
      })
      .addCase(fetchTreatmentPlan.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // CREATE
      .addCase(createTreatmentPlan.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createTreatmentPlan.fulfilled, (state, action) => {
        state.isLoading = false;
        state.treatmentPlans.unshift(action.payload);
      })
      .addCase(createTreatmentPlan.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // UPDATE
      .addCase(updateTreatmentPlan.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateTreatmentPlan.fulfilled, (state, action) => {
        state.isLoading = false;
        const updated = action.payload.data ?? action.payload;

        const index = state.treatmentPlans.findIndex(
          (p) => p.id === updated.id
        );
        if (index !== -1) {
          state.treatmentPlans[index] = updated;
        }

        if (state.currentPlan?.id === updated.id) {
          state.currentPlan = updated;
        }
      })
      .addCase(updateTreatmentPlan.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // DELETE
      .addCase(deleteTreatmentPlan.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteTreatmentPlan.fulfilled, (state, action) => {
        state.isLoading = false;
        state.treatmentPlans = state.treatmentPlans.filter(
          (p) => p.id !== action.payload
        );

        if (state.currentPlan?.id === action.payload) {
          state.currentPlan = null;
        }
      })
      .addCase(deleteTreatmentPlan.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, setCurrentPlan, clearCurrentPlan } = treatmentPlanSlice.actions;

export default treatmentPlanSlice.reducer;
